import { motion } from 'framer-motion';
import { Flame, Zap, Trophy, Star, Gift, Share, UserPlus, CheckCircle, Calendar, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import type { UserTask } from '@/hooks/useGamification';

interface TaskCardProps {
    task: UserTask;
    onClaim?: () => void;
    onComplete?: () => void;
    isLoading?: boolean;
}

const iconMap: Record<string, React.ElementType> = {
    'flame': Flame,
    'zap': Zap,
    'trophy': Trophy,
    'star': Star,
    'gift': Gift,
    'share': Share,
    'user-plus': UserPlus,
    'calendar-check': Calendar,
    'sparkles': Sparkles,
    'user-check': CheckCircle,
};

export function TaskCard({ task, onClaim, onComplete, isLoading }: TaskCardProps) {
    const Icon = iconMap[task.icon] || Star;
    const isCompleted = task.status === 'completed';
    const isClaimed = task.status === 'claimed';

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
        >
            <Card className={`overflow-hidden transition-all ${isCompleted
                    ? 'bg-primary/5 border-primary/20'
                    : 'hover:shadow-md hover:border-primary/30'
                }`}>
                <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${isCompleted
                                ? 'bg-primary/20'
                                : 'bg-gradient-to-br from-primary/20 to-primary/10'
                            }`}>
                            <Icon className={`w-6 h-6 ${isCompleted ? 'text-primary' : 'text-primary'}`} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className={`font-medium truncate ${isCompleted ? 'text-muted-foreground' : ''}`}>
                                    {task.title}
                                </h3>
                                {isCompleted && (
                                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                                )}
                            </div>

                            <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                                {task.description}
                            </p>

                            {/* Rewards */}
                            <div className="flex items-center gap-3 text-sm">
                                {task.xp_reward > 0 && (
                                    <span className="flex items-center gap-1 text-amber-500">
                                        <Zap className="w-3.5 h-3.5" />
                                        <span className="font-medium">{task.xp_reward} XP</span>
                                    </span>
                                )}
                                {task.credit_reward > 0 && (
                                    <span className="flex items-center gap-1 text-emerald-500">
                                        <Gift className="w-3.5 h-3.5" />
                                        <span className="font-medium">{task.credit_reward} crédito{task.credit_reward > 1 ? 's' : ''}</span>
                                    </span>
                                )}
                            </div>

                            {/* Progress bar for multi-step tasks */}
                            {task.progress_max > 1 && (
                                <div className="mt-2">
                                    <Progress
                                        value={(task.progress / task.progress_max) * 100}
                                        className="h-1.5"
                                    />
                                    <span className="text-xs text-muted-foreground mt-1">
                                        {task.progress}/{task.progress_max}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Action Button */}
                        {!isCompleted && (
                            <div className="flex-shrink-0">
                                {isClaimed ? (
                                    <Button
                                        size="sm"
                                        onClick={onComplete}
                                        disabled={isLoading}
                                        className="h-8"
                                    >
                                        {isLoading ? '...' : 'Completar'}
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={onClaim}
                                        disabled={isLoading}
                                        className="h-8"
                                    >
                                        {isLoading ? '...' : 'Começar'}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}
